import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { ConditionWithInfoScreen, shouldShowConditionInfoScreen } from '@component/condition_info_screen.component';
import { ReceptionConditionInfoScreenFlow } from '@visiba/api-vcs-client-v2';
import { useResolve } from '@visiba/instantiation';
import { useCallback, useEffect } from 'react';
import { BookingScreen, useBooking, ViewBookingAnalyticActions } from '../../hooks/use_booking';
import { useBookingBasket } from '../../hooks/use_booking_basket';

export function useReasonForVisit(onInfoScreen: (condition: ConditionWithInfoScreen) => void) {
	const viewBooking = useBooking();
	const analyticService = useResolve(AnalyticService);
	const bookingBasket = useBookingBasket();

	const selectReasonForVisit = useCallback(
		async (reasonForVisitId: number, redirect = true) => {
			analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.SelectReasonForVisit, `${reasonForVisitId}`);
			await bookingBasket.setBasket({ reasonForVisit: reasonForVisitId });
			if (redirect) viewBooking.goToScreen(BookingScreen.Location);
		},
		[analyticService, bookingBasket.setBasket, viewBooking.goToScreen],
	);

	useEffect(() => {
		analyticService.trackPageView({ customTitle: 'Booking: Reason for visit' });
		bookingBasket.unlockTimeSlot();
		if (viewBooking.flowData.status !== 'accessible' || bookingBasket.getBasket().respondantType == null) {
			analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.NotAllowedToBeHere, 'Reason for visit');
			viewBooking.goToScreen(BookingScreen.WhoIsRespondant, { replace: true });
			return;
		}

		const reasonForVisits = viewBooking.getFilteredReasonForVisits();
		if (reasonForVisits.length === 0) {
			analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.NoReasonForVisits);
			viewBooking.goToScreen(BookingScreen.Location, { replace: true });
			return;
		}
		if (reasonForVisits.length === 1) {
			const reasonForVisit = reasonForVisits[0];
			if (shouldShowConditionInfoScreen(reasonForVisit, ReceptionConditionInfoScreenFlow.Booking)) {
				onInfoScreen(reasonForVisit);
				return;
			}

			analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.AutoSetReasonForVisit, `${reasonForVisit.Id}`);
			selectReasonForVisit(reasonForVisit.Id, false).then(() => viewBooking.goToScreen(BookingScreen.Location, { replace: true }));
		}
	}, [
		analyticService,
		bookingBasket.unlockTimeSlot,
		bookingBasket.getBasket,
		viewBooking.flowData,
		viewBooking.getFilteredReasonForVisits,
		viewBooking.goToScreen,
		onInfoScreen,
		selectReasonForVisit,
	]);

	return { selectReasonForVisit };
}
